/* eslint-disable no-unused-vars */
import React, {useState, useEffect, useCallback} from 'react'
import {useNavigate, useLocation} from 'react-router-dom'
import {useIntl} from 'react-intl'
import * as Yup from 'yup'
import {useFormik} from 'formik'

import {KTCard, KTCardBody, KTCardHeader, Select, InputPrice, Loading, Datepicker, HelpIcon} from '../../../../components'
import {PageTitle} from '../../../../core'

import {useDispatch, useSelector} from 'react-redux'
import {
  projectLookup,
  bankCashAccountGetAll,
  basicLookup,
} from '../../../../redux/slices/lookup.slice'
import {categoryGetAll} from '../../../../redux/slices/category.slice'
import {expenseinvoiceTaxCreate} from '../../../../redux/slices/expenseinvoice.slice'
import {
  useMemoizedPageUrl,
  useMemoizedValidationSchema,
  useMemoizedFormValues,
  useMemoizedBreadcrumbs
} from '../../../../utils/performanceOptimizations'

const selectLookupData = (state) => ({
  projectLookupList: state.lookup.projectLookupList,
  accountLookupList: state.lookup.accountLookupList,
  ExpensePaymentStatus: state.lookup.ExpensePaymentStatus,
  InvoiceKind: state.lookup.InvoiceKind,
  loading: state.lookup.loading
})

const selectExpenseInvoiceData = (state) => ({
  isRedirect: state.expenseinvoice.isRedirect,
  loading: state.expenseinvoice.loading
})

const selectCategoryData = (state) => ({
  category: state.category.category
})

function Create() {
  const intl = useIntl()
  const navigate = useNavigate()
  const location = useLocation()
  const pageUrl = useMemoizedPageUrl(location)

  const {projectLookupList, accountLookupList, ExpensePaymentStatus, InvoiceKind, loading} =
    useSelector(selectLookupData)
  const {isRedirect, loading: createLoading} = useSelector(selectExpenseInvoiceData)
  const {category} = useSelector(selectCategoryData)
  const dispatch = useDispatch()

  useEffect(() => {
    dispatch(projectLookup())
    dispatch(bankCashAccountGetAll())
    dispatch(basicLookup('ExpensePaymentStatus'))
    dispatch(categoryGetAll({SourceTypeId: 8}))
    dispatch(basicLookup('InvoiceKind'))
  }, [dispatch])

  const validSchema = useMemoizedValidationSchema(() =>
    Yup.object().shape({
      Name: Yup.string().required(intl.formatMessage({id: 'REQUIRED.GENERAL'})),
      ProjectId: Yup.string().required(intl.formatMessage({id: 'REQUIRED.GENERAL'})),
      InvoiceKindId: Yup.string().required(intl.formatMessage({id: 'REQUIRED.GENERAL'})),
    }), [intl]
  )

  const [expensePaymentStatus, setExpensePaymentStatus] = useState()

  const formValues = useMemoizedFormValues({
    Name: '',
    ProjectId: null,
    EditDate: new Date(),
    DefermentDate: new Date(),
    TotalPrice: 0,
    ExpensePaymentStatusId: null,
    FromAccountId: null,
    CheckoutName: '',
    PaymentDate: new Date(),
    CategoryId: null,
    InvoiceKindId: '',
  })

  const handleSubmit = useCallback(async (values, {setSubmitting, resetForm}) => {
    setSubmitting(true)
    dispatch(expenseinvoiceTaxCreate(values))
    resetForm()
  }, [dispatch])

  const formik = useFormik({
    initialValues: formValues,
    validationSchema: validSchema,
    onSubmit: handleSubmit,
  })

  const breadcrumbs = useMemoizedBreadcrumbs(() => [
    {
      title: intl.formatMessage({id: 'BC.EXPENSES'}),
      path: '/expenses/expenses',
      isSeparator: false,
      isActive: false,
    },
    {
      title: '',
      path: '',
      isSeparator: true,
      isActive: false,
    },
  ], [intl])

  const handleRedirect = useCallback(() => {
    if (isRedirect && isRedirect.id) {
      navigate(`/${pageUrl[1]}/${pageUrl[2]}/${pageUrl[3]}/detail/${isRedirect.id}`)
    }
  }, [isRedirect, navigate, pageUrl])

  useEffect(() => {
    handleRedirect()
  }, [handleRedirect])

  return (
    <>
      <PageTitle breadcrumbs={breadcrumbs}>
        {intl.formatMessage({id: 'BC.EXPENSES.TAX.ADD'})}
      </PageTitle>
      <KTCard>
        <KTCardHeader title={intl.formatMessage({id: 'BC.EXPENSES.TAX.ADD'})}>
          <HelpIcon articleId='expense-entry' />
        </KTCardHeader>
        <KTCardBody>
          <form className='form' onSubmit={formik.handleSubmit} noValidate>
            <div className='row mb-6'>
              <label className='col-lg-3 fw-bold fs-6 mb-2 required'>
                {intl.formatMessage({id: 'EXPENSEINVOICE.CREATETAX.NAME'})}
              </label>
              <div className='col-lg-6 fv-row fv-plugins-icon-container'>
                <input
                  type='text'
                  required
                  className='form-control form-control-solid mb-3 mb-lg-0'
                  autoComplete='off'
                  disabled={formik.isSubmitting}
                  {...formik.getFieldProps('Name')}
                />
                {formik.touched.Name && formik.errors.Name && (
                  <div className='fv-plugins-message-container'>
                    <div className='fv-help-block'>
                      <span role='alert'>{formik.errors.Name}</span>
                    </div>
                  </div>
                )}
              </div>
            </div>
            <div className='row mb-6'>
              <label className='col-lg-3 fw-bold fs-6 mb-2'>
                {intl.formatMessage({id: 'EXPENSEINVOICE.CREATETAX.CATEGORY'})}
              </label>
              <div className='col-lg-6 fv-row fv-plugins-icon-container'>
                <Select
                  data={category}
                  {...formik.getFieldProps('CategoryId')}
                  onChange={(item) => formik.setFieldValue('CategoryId', Number(item && item.value))}
                />
              </div>
            </div>
            <div className='row mb-6'>
              <label className='col-lg-3 fw-bold fs-6 mb-2 required'>
                {intl.formatMessage({id: 'EXPENSEINVOICE.CREATETAX.PROJECT'})}
              </label>
              <div className='col-lg-6 fv-row fv-plugins-icon-container'>
                <Select
                  data={projectLookupList}
                  required
                  {...formik.getFieldProps('ProjectId')}
                  onChange={(item) => formik.setFieldValue('ProjectId', Number(item && item.value))}
                />
                {formik.touched.ProjectId && formik.errors.ProjectId && (
                  <div className='fv-plugins-message-container'>
                    <div className='fv-help-block'>
                      <span role='alert'>{formik.errors.ProjectId}</span>
                    </div>
                  </div>
                )}
              </div>
            </div>
            <div className='row mb-6'>
              <label className='col-lg-3 fw-bold fs-6 mb-2 required'>
                {intl.formatMessage({id: 'SALEINVOICE.CREATE.INVOICEKINDNAME'})}
              </label>
              <div className='col-lg-6 fv-row fv-plugins-icon-container'>
                <Select
                  data={InvoiceKind}
                  required
                  {...formik.getFieldProps('InvoiceKindId')}
                  onChange={(item) => formik.setFieldValue('InvoiceKindId', Number(item && item.value))}
                />
                {formik.touched.InvoiceKind && formik.errors.InvoiceKind && (
                  <div className='fv-plugins-message-container'>
                    <div className='fv-help-block'>
                      <span role='alert'>{formik.errors.InvoiceKind}</span>
                    </div>
                  </div>
                )}
              </div>
            </div>
            <div className='row mb-6'>
              <label className='col-lg-3 fw-bold fs-6 mb-2'>
                {intl.formatMessage({id: 'EXPENSEINVOICE.CREATETAX.EDITDATE'})}
              </label>
              <div className='col-lg-6 fv-row fv-plugins-icon-container'>
                <Datepicker
                  {...formik.getFieldProps('EditDate')}
                  onChange={([date]) => formik.setFieldValue('EditDate', date)}
                />
              </div>
            </div>
            <div className='row mb-6'>
              <label className='col-lg-3 fw-bold fs-6 mb-2'>
                {intl.formatMessage({id: 'EXPENSEINVOICE.CREATETAX.DEFERMENTDATE'})}
              </label>
              <div className='col-lg-6 fv-row fv-plugins-icon-container'>
                <Datepicker
                  {...formik.getFieldProps('DefermentDate')}
                  onChange={([date]) => formik.setFieldValue('DefermentDate', date)}
                />
              </div>
            </div>
            <div className='row mb-6'>
              <label className='col-lg-3 fw-bold fs-6 mb-2'>
                {intl.formatMessage({id: 'EXPENSEINVOICE.CREATETAX.TOTALPRICE'})}
              </label>
              <div className='col-lg-6 fv-row fv-plugins-icon-container'>
                <InputPrice
                  {...formik.getFieldProps('TotalPrice')}
                  onChange={(value) => formik.setFieldValue('TotalPrice', Number(value))}
                />
              </div>
            </div>
            <div className='row mb-6'>
              <label className='col-lg-3 fw-bold fs-6 mb-2'>
                {intl.formatMessage({id: 'EXPENSEINVOICE.CREATETAX.EXPENSEPAYMENTSTATUS'})}
              </label>
              <div className='col-lg-6 fv-row fv-plugins-icon-container'>
                <Select
                  data={ExpensePaymentStatus}
                  {...formik.getFieldProps('ExpensePaymentStatusId')}
                  onChange={(item) => {
                    formik.setFieldValue('ExpensePaymentStatusId', Number(item?.value))
                    setExpensePaymentStatus(Number(item.value))
                  }}
                />
              </div>
            </div>
            {expensePaymentStatus == '2' && (
              <>
                {' '}
                <div className='row mb-6'>
                  <label className='col-lg-3 fw-bold fs-6 mb-2'>
                    {intl.formatMessage({id: 'EXPENSEINVOICE.CREATETAX.ACCOUNT'})}
                  </label>
                  <div className='col-lg-6 fv-row fv-plugins-icon-container'>
                    <Select
                      data={accountLookupList}
                      {...formik.getFieldProps('FromAccountId')}
                      onChange={(item) =>
                        formik.setFieldValue('FromAccountId', Number(item?.value))
                      }
                    />
                  </div>
                </div>
                <div className='row mb-6'>
                  <label className='col-lg-3 fw-bold fs-6 mb-2'>
                    {intl.formatMessage({id: 'EXPENSEINVOICE.CREATETAX.CHECKOUTNAME'})}
                  </label>
                  <div className='col-lg-6 fv-row fv-plugins-icon-container'>
                    <input
                      type='text'
                      className='form-control form-control-solid mb-3 mb-lg-0'
                      autoComplete='off'
                      disabled={formik.isSubmitting}
                      {...formik.getFieldProps('CheckoutName')}
                    />
                  </div>
                </div>
                <div className='row mb-6'>
                  <label className='col-lg-3 fw-bold fs-6 mb-2'>
                    {intl.formatMessage({id: 'EXPENSEINVOICE.CREATETAX.PAYMENTDATE'})}
                  </label>
                  <div className='col-lg-6 fv-row fv-plugins-icon-container'>
                    <Datepicker
                      {...formik.getFieldProps('PaymentDate')}
                      onChange={([date]) => formik.setFieldValue('PaymentDate', date)}
                    />
                  </div>
                </div>
              </>
            )}
            <div className='row pt-15'>
              <div className='col-lg-9 fv-row fv-plugins-icon-container d-flex gap-3 justify-content-center'>
                <button
                  className='btn btn-light'
                  onClick={() => navigate(`/${pageUrl[1]}/${pageUrl[2]}`)}
                >
                  {intl.formatMessage({id: 'BUTTON.CANCEL'})}
                </button>
                <button
                  type='submit'
                  className='btn btn-primary'
                  data-kt-users-modal-action='submit'
                  disabled={formik.isSubmitting || !formik.isValid || !formik.touched}
                >
                  <span className='indicator-label'>
                    {intl.formatMessage({id: 'BUTTON.SUBMIT'})}
                  </span>
                  {formik.isSubmitting && (
                    <span className='indicator-progress'>
                      {intl.formatMessage({id: 'PLEASE'})}
                      <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
                    </span>
                  )}
                </button>
              </div>
            </div>
          </form>
        </KTCardBody>
      </KTCard>

      {(loading | createLoading) > 0 && <Loading />}
    </>
  )
}
export {Create}
